import {
    AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    effect,
    ElementRef, inject,
    input,
    output,
    signal,
    viewChild
} from '@angular/core';
import {CellComment, CheckUtils} from '../../data/app-data-checks';
import {AccountData} from '../../../api-client-kt/src';
import {
    CollectionView,
    ICollectionView,
    NotifyCollectionChangedAction,
    NotifyCollectionChangedEventArgs
} from '@mescius/wijmo';
import {TextInput} from '@fluentui/web-components';
import {
    AccountCommentsEditorComponent,
    AccountDataNotifyCollectionChangedEventArgs
} from '../account-comments-editor/account-comments-editor.component';
import {Stdlib} from '../../lib/stdlib';
import {DataDictionaryService} from '../../data/data-dictionary';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';

export interface AccountFieldComments {
    id: string;
    timestamp: Date;
    multifield: boolean
    hidden: boolean;
    account: AccountData;
    attribute: string;
    attributeName: string;
}

/**
 * AccountCommentsPaneComponent is responsible for displaying and managing account-related comments.
 * It provides functionality to filter, extract, and display comments associated with account attributes.
 * The component is designed with an OnPush change detection strategy for improved performance.
 *
 * Inputs/Outputs:
 * - `hidden`: Input property controlling the visibility of the pane. Used to optimize DOM.
 * - `accountsCv`: Required input property representing the collection view of account data.
 * - `onCellPaneCommentClicked`: Output event that emits when a user clicks on a specific account's comment pane.
 */
@Component({
    selector: 'app-account-comments-pane',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AccountCommentsEditorComponent
    ],
    templateUrl: './account-comments-pane.component.html',
    styleUrl: './account-comments-pane.component.scss',
})
export class AccountCommentsPaneComponent implements AfterViewInit {

    hidden = input(true);
    accountsCv = input.required<CollectionView<AccountData>>();
    onCellPaneCommentClicked = output<AccountFieldComments>();

    private readonly pfSegData = inject(PortfolioSegmentDataContext);
    protected readonly segment = this.pfSegData.activeSegment.value;

    protected accountsWithComments = signal<AccountFieldComments[]>([]);

    private readonly filterTextInput = viewChild<ElementRef<TextInput>>('filterTextInput');
    private readonly commentsContainer = viewChild<ElementRef<HTMLDivElement>>('commentsContainer');

    private readonly accountsCvChangedHandler = this.onCollectionViewChanged.bind(this);

    constructor() {
        effect(() => this.registerChange());
    }

    private registerChange() {
        const accountDataCollectionView = this.accountsCv();
        accountDataCollectionView.collectionChanged.removeHandler(this.accountsCvChangedHandler);
        accountDataCollectionView.collectionChanged.addHandler(this.accountsCvChangedHandler);
        this.extractCellComments();
    }

    ngAfterViewInit() {
    }

    private onCollectionViewChanged(sender: ICollectionView<AccountData>, e: NotifyCollectionChangedEventArgs) {
        if (e instanceof AccountDataNotifyCollectionChangedEventArgs && e.action === NotifyCollectionChangedAction.Change) {
            this.extractCellComments();
        }
    }

    private extractCellComments() {
        const extracted: AccountFieldComments[] = [];
        const accountsWithComments = this.accountsCv().sourceCollection
            .filter(a => CheckUtils.getAllComments(a).length > 0);

        for (const account of accountsWithComments) {

            // extract all the comments in an object, where the key is the attribute name
            const commentsByAttr = CheckUtils.getAllCommentsGroupedByAttribute(account);

            // generate a timestamp of the latest comment in the entire account (across all attributes)
            const commentDates = Object.values(commentsByAttr)
                .flatMap(comments => comments.map(c => Stdlib.toDate(c.createdAt) ?? Stdlib.MIN_DATE));

            const latestCommentDate = Stdlib.maxOfDates(commentDates) ?? Stdlib.MIN_DATE;

            // we will calculate a hash of the comments to trigger a UI update
            // when the hash changes. however, this is super simple a so naive
            // that it is rather embarrassing to use it. it will do the job for now.
            let naiveHash = 0;

            const entries = Object.entries(commentsByAttr);
            for (const [attribute, comments] of entries) {
                const attributeName = DataDictionaryService.getAccountDataAttribute(attribute)?.name;
                comments.forEach(comment => naiveHash += comment.content.length);

                if (comments.length > 0) {
                    extracted.push({
                        id: `${account.account}-${attribute}-${latestCommentDate.getTime()}-${naiveHash}`,
                        timestamp: latestCommentDate,
                        hidden: false,
                        account: account,
                        attribute: attribute,
                        attributeName: attributeName ?? '',
                        multifield: entries.length > 1
                    });
                }
            }
        }
        extracted.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        this.accountsWithComments.set(extracted);
        this.clearFilter(this.filterTextInput()?.nativeElement, this.commentsContainer()?.nativeElement);
    }

    protected onCellPaneCommentClick(comment: AccountFieldComments) {
        this.onCellPaneCommentClicked.emit(comment);
    }

    protected clearFilter(element: HTMLElement | undefined, commentsContainer: HTMLElement | undefined) {
        if (!element || !commentsContainer) return;
        const filterTextInput = element as TextInput;
        filterTextInput.value = '';
        this.onFilterChange(element, commentsContainer);
    }

    protected onFilterChange(element: HTMLElement, commentsContainer: HTMLElement) {
        const filterTextInput = element as TextInput;
        const filterText = filterTextInput.value.toLowerCase();
        const allComments = this.accountsWithComments();
        const ranges: Range[] = [];

        commentsContainer.querySelectorAll<HTMLElement>('.comment-box').forEach((box, index) => {
            const textInCommentBox = box.innerText.toLowerCase();
            const isVisible = textInCommentBox.includes(filterText);
            box.style.display = isVisible ? 'block' : 'none';
            allComments[index].hidden = !isVisible;

            if (filterText && isVisible) {
                ranges.push(...this.addHighlightsForMatches(box, filterText));
            }
        });

        if (CSS.highlights) {
            CSS.highlights.set('search-match', new Highlight(...ranges));
        }
    }

    /**
     * Determines ranges for highlighting the occurrences of the specified filter text within
     * the given root node by creating a range for each match.
     *
     * @param {Node} rootNode - The root node within which the text matches are searched.
     * @param {string} filterText - The text to search for and highlight. The search is case-insensitive.
     * @return {Range[]} An array of Range objects representing the positions of all matches.
     */
    private addHighlightsForMatches(rootNode: Node, filterText: string): Range[] {
        const ranges: Range[] = [];
        const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT);
        let currentNode = walker.nextNode();

        while (currentNode) {
            const textContent = currentNode.textContent?.toLowerCase() || '';
            let startIndex = 0;
            while ((startIndex = textContent.indexOf(filterText, startIndex)) !== -1) {
                const range = new Range();
                range.setStart(currentNode, startIndex);
                range.setEnd(currentNode, startIndex + filterText.length);
                ranges.push(range);
                startIndex += filterText.length;
            }
            currentNode = walker.nextNode();
        }
        return ranges;
    }

    protected onExtractComments() {
        this.extractCellComments();
    }

    protected onCommentsUpdated(a: AccountFieldComments) {
        const account = a.account;
        const event = new AccountDataNotifyCollectionChangedEventArgs(NotifyCollectionChangedAction.Change, account, "metadataComments");
        this.accountsCv().onCollectionChanged(event);
    }
}
